import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as fabric from 'fabric'
import _ from 'lodash'
import {
  DEFAULT_OBJECT_CONTROL,
  MAX_ZOOM,
  MIN_ZOOM,
  clampPan,
  generateUniqueId,
  isContained,
  normalizeHexColor,
} from '@utils/editor-bozze'
import {
  ArticoloType,
  EditorColorType,
  EditorColoreApiType,
  EditorImageType,
  EditorObjectType,
  EditorTextType,
} from '@utils/types'
import { getPantoneHex } from '@utils/editor-bozze/colors'
import {
  applyTranslucentFinishToCanvasAndRender,
  isTranslucentFinishEnabledForTecnica,
} from '@utils/editor-bozze/fabric-editor-translucent-finish'
import {
  CANVAS_BACKGROUND_ID,
  CANVAS_CENTER_OFFSET,
  FABRIC_CANVAS_SIZE,
  PAN_PADDING,
  PREVIEW_HIDDEN_OBJECT_IDS,
  PREVIEW_OUTPUT_HEIGHT,
  PREVIEW_OUTPUT_WIDTH,
  PREVIEW_STRIP_WIDTH,
  PRINT_AREA_SIZE,
  ROTATION_SNAP_STEP,
  SECONDARY_PREVIEW_MAX_SIZE,
  SPECIAL_CANVAS_OBJECT_IDS,
  TEXT_TYPES,
  TRANSFORM_TEXT_PROPS,
  applyCornerOnlyControls,
  applyTransformState,
  clampRotatingToContainedArea,
  clampScalingToContainedArea,
  clampTextFontSizeToContainedArea,
  getAngleFromCenterToPointer,
  getAreaStampaImageUrl,
  getContainmentDeltaInAreaSpace,
  getEffectiveTextFontSize,
  getMagneticSnapAngle,
  getObjectBoundsInAreaSpace,
  getObjectEditorMeta,
  getPointInAreaSpace,
  getSnappedAngle,
  getTransformState,
  getVisualObjectDimensionsInCm,
  isRotatingAction,
  isScalingAction,
  normalizeAngle,
  normalizeTextScaleIntoFontSize,
  refreshTextObjectDimensions,
  setObjectAngleAroundCenter,
  shrinkObjectToFitAreaBounds,
  storeCurrentTransformStateIfValid,
  translateObjectInAreaSpace,
} from '@utils/editor-bozze/fabric-editor-helpers'
import {
  addPrintAreaQuote,
  createQuoteLabelGroup,
} from '@utils/editor-bozze/fabric-editor-quote'
import {
  buildPreviewByColorId as buildPreviewByColorIdFromModule,
  composeMultiColorPreview as composeMultiColorPreviewFromModule,
  downloadPreviewDataUrl,
  exportCanvasDataUrl as exportCanvasDataUrlFromModule,
  getBackgroundOriginFromImage,
  getCanvasBoundsFromBackground,
  getExportBoundsFromBackground,
  getPreviewImage as getPreviewImageFromModule,
  getPrintAreaBoundsRelativeToBackground as getPrintAreaBoundsRelativeToBackgroundFromObjects,
  getSecondaryPreviewItems as getSecondaryPreviewItemsFromModule,
} from '@utils/editor-bozze/fabric-editor-export-preview'
import {
  alignFabricObject,
  applyObjectPropertyChange,
  deleteActiveFabricObject,
  deleteFabricObjectById,
  dispatchObjectAction,
  removeFabricObject as removeFabricObjectFromModule,
} from '@utils/editor-bozze/fabric-editor-object-actions'
import {
  loadImageObject,
  loadSvgFromUrl as loadSvgFromUrlFromModule,
  loadSvgToCanvas as loadSvgToCanvasFromModule,
  loadTextObject,
} from '@utils/editor-bozze/fabric-editor-object-loading'
import {
  applyColorMappingsToFabricObject,
  buildColorMappings,
  extractObjectColors,
  findBackgroundObject as findBackgroundObjectFromModule,
  freezeOriginalColors as freezeOriginalColorsFromModule,
  getPreferredColorVariant,
  hasObjectControlVariantMismatch,
  handleColorChange as handleColorChangeFromModule,
  handleMonochromaticChange as handleMonochromaticChangeFromModule,
  handleNoBackgroundChange as handleNoBackgroundChangeFromModule,
  normalizeObjectControlColorVariant,
  resetObjectToOriginalColor,
} from '@utils/editor-bozze/fabric-editor-colors'
import {
  handleColorVisibilityChange as handleColorVisibilityChangeFromModule,
  syncHiddenColorsDataTransform,
} from '@utils/editor-bozze/fabric-editor-hidden-colors'
import {
  loadCanvasFromJson as loadCanvasFromJsonFromModule,
  replaceCanvasJson as replaceCanvasJsonFromModule,
  serializeCanvasJson as serializeCanvasJsonFromModule,
} from '@utils/editor-bozze/fabric-editor-transfer'
import { useDebounceCallback } from './useDebounceCallback'
import { CanvasActions, useEditorContext } from 'context/EditorContext'

export const useFabricEditor = (
  personalizzazioneData: any,
  colore?: EditorColorType,
  isVisible = false,
  preview = false,
  measureMode = false,
  articoli: ArticoloType[] = [] // Serve per comporre nell'export finale anche gli altri articoli/colori disponibili.
) => {
  /**
   * Struttura di lettura del hook:
   * 1) Input/contesto
   * 2) Refs runtime
   * 3) Stato React
   * 4) Memo derivati
   * 5) Callback per feature (viewport, interazione, preview, oggetti, colori, transfer)
   * 6) useEffect di sincronizzazione/lifecycle
   * 7) API pubblica esposta al resto dell'editor
   */
  // ============================================================================
  // Input e contesto esterno
  // ============================================================================
  const {
    registerActions,
    unregisterActions,
    updateCanvasData,
    activeCanvasId,
    colorLimits,
    pantoni,
  } = useEditorContext()

  const larghezzaAreaStampa = personalizzazioneData?.configurazione?.dimensione_larghezza
  const altezzaAreaStampa = personalizzazioneData?.configurazione?.dimensione_altezza

  // ============================================================================
  // Refs runtime Fabric/interaction
  // ============================================================================
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasElRef = useRef<HTMLCanvasElement>(null)
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null)
  const areaStampaRef = useRef<fabric.Object | null>(null)
  const backgroundImageRef = useRef<fabric.FabricImage | null>(null)
  const measurementAnchorRef = useRef<fabric.Point | null>(null)
  const measurementObjectRef = useRef<fabric.Group | null>(null)

  const preTransformStateRef = useRef(null)
  const activeRotationGestureRef = useRef<{
    target: fabric.Object | null
    // Pivot congelato all'inizio della gesture: anche se il containment trasla
    // l'oggetto, il calcolo dell'angolo continua rispetto a questo centro iniziale.
    pivotCenter: fabric.Point
    // Differenza iniziale tra angle oggetto e angle puntatore. Mantenerla costante
    // evita "salti" quando il drag parte con il mouse non perfettamente allineato.
    angleOffset: number
  } | null>(null)
  // Stato temporaneo della rotazione in corso: ricorda il cardinale agganciato
  // per applicare isteresi solo durante la gesture attiva.
  const activeRotationSnapRef = useRef<{
    target: fabric.Object | null
    angle: number | null
  } | null>(null)
  const minZoomRef = useRef(MIN_ZOOM)
  const appliedViewportSizeRef = useRef({ width: 0, height: 0 })
  const loghiRefs = useRef<fabric.Object[]>([])
  const previewRef = useRef(preview)
  const measureModeRef = useRef(measureMode)
  const preferredColorVariantRef = useRef<EditorColorType['variant']>('pantone')
  const pantoneReferenceRef = useRef<EditorColoreApiType[]>([])
  const previousForcedSingleColorHexRef = useRef('')
  const areaDimensionsRef = useRef({
    larghezzaAreaStampa,
    altezzaAreaStampa,
  })
  const loadedAreaColorIdRef = useRef<number | string | null>(colore?.id ?? null)

  const clearMeasurement = useCallback(() => {
    const canvas = fabricCanvasRef.current
    if (canvas && measurementObjectRef.current) {
      canvas.remove(measurementObjectRef.current)
      canvas.requestRenderAll()
    }

    measurementAnchorRef.current = null
    measurementObjectRef.current = null
  }, [])

  const buildMeasurementObject = useCallback(
    (startPoint: fabric.Point, endPoint: fabric.Point) => {
      const measurementColor = '#00C2FF'
      const areaStampa = areaStampaRef.current
      const areaWidthCm = Number(larghezzaAreaStampa) || 0
      const areaHeightCm = Number(altezzaAreaStampa) || 0

      let distanceLabel = ''
      if (areaStampa && areaWidthCm > 0 && areaHeightCm > 0) {
        const startInArea = getPointInAreaSpace(startPoint, areaStampa)
        const endInArea = getPointInAreaSpace(endPoint, areaStampa)
        const { areaBounds } = getObjectBoundsInAreaSpace(areaStampa, areaStampa)
        const areaWidthPx = areaBounds.maxX - areaBounds.minX
        const areaHeightPx = areaBounds.maxY - areaBounds.minY

        if (areaWidthPx > 0 && areaHeightPx > 0) {
          const dxCm = ((endInArea.x - startInArea.x) / areaWidthPx) * areaWidthCm
          const dyCm = ((endInArea.y - startInArea.y) / areaHeightPx) * areaHeightCm
          const distanceCm = Math.sqrt(dxCm * dxCm + dyCm * dyCm)
          if (Number.isFinite(distanceCm)) {
            const roundedDistanceCm = Number(distanceCm.toFixed(1))
            distanceLabel = `${roundedDistanceCm} cm`
          }
        }
      }

      const objects: fabric.Object[] = [
        new fabric.Line([startPoint.x, startPoint.y, endPoint.x, endPoint.y], {
          stroke: measurementColor,
          strokeWidth: 10,
          strokeDashArray: [32, 20],
          selectable: false,
          evented: false,
          excludeFromExport: true,
        }),
        new fabric.Circle({
          radius: 18,
          fill: measurementColor,
          stroke: '#ffffff',
          strokeWidth: 4,
          left: startPoint.x,
          top: startPoint.y,
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          excludeFromExport: true,
        }),
        new fabric.Circle({
          radius: 18,
          fill: measurementColor,
          stroke: '#ffffff',
          strokeWidth: 4,
          left: endPoint.x,
          top: endPoint.y,
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          excludeFromExport: true,
        }),
      ]

      if (distanceLabel) {
        const dx = endPoint.x - startPoint.x
        const dy = endPoint.y - startPoint.y
        const isMostlyVertical = Math.abs(dy) > Math.abs(dx)
        const labelGroup = createQuoteLabelGroup(distanceLabel, {
          stroke: measurementColor,
          strokeWidth: 6,
        })
        labelGroup.set({
          left: (startPoint.x + endPoint.x) / 2 + (isMostlyVertical ? 56 : 0),
          top: (startPoint.y + endPoint.y) / 2 + (isMostlyVertical ? 0 : 56),
          originX: isMostlyVertical ? 'left' : 'center',
          originY: isMostlyVertical ? 'center' : 'top',
          excludeFromImage: true,
        } as any)
        objects.push(labelGroup)
      }

      return new fabric.Group(objects, {
        customId: 'measurement-line',
        selectable: false,
        evented: false,
        excludeFromExport: true,
      })
    },
    [altezzaAreaStampa, larghezzaAreaStampa]
  )

  const renderMeasurement = useCallback(
    (startPoint: fabric.Point, endPoint: fabric.Point) => {
      const canvas = fabricCanvasRef.current
      if (!canvas) return

      if (measurementObjectRef.current) {
        canvas.remove(measurementObjectRef.current)
      }

      const measurementObject = buildMeasurementObject(startPoint, endPoint)
      measurementObjectRef.current = measurementObject
      canvas.add(measurementObject)
      canvas.requestRenderAll()
    },
    [buildMeasurementObject]
  )

  // ============================================================================
  // Stato React sincronizzato con UI/sidebar
  // ============================================================================
  const [initialObjectControls, setInitialObjectControls] = useState<EditorObjectType[]>([])
  const [objectControls, setObjectControls] = useState<EditorObjectType[]>([])
  const [previewImage, setPreviewImage] = useState<string>()
  const [previewByColorId, setPreviewByColorId] = useState<Record<string, string>>({})

  useEffect(() => {
    measureModeRef.current = measureMode
  }, [measureMode])

  // ============================================================================
  // Memo derivati (configurazione/stato editor)
  // ============================================================================
  const configurazioneNumeroColori =
    personalizzazioneData?.configurazione?.numero_colori ||
    personalizzazioneData?.configurazione?.numeroColori
  const preferredColorVariant: EditorColorType['variant'] = getPreferredColorVariant(
    configurazioneNumeroColori
  )
  const fullPantoneReference = useMemo(
    () => (Array.isArray(pantoni) ? pantoni.filter(Boolean) : []),
    [pantoni]
  )
  const tecnicaPantoniConsentiti = useMemo(
    () =>
      Array.isArray(personalizzazioneData?.configurazione?.tecnica?.editor_pantoni_consentiti)
        ? personalizzazioneData.configurazione.tecnica.editor_pantoni_consentiti.filter(Boolean)
        : [],
    [personalizzazioneData?.configurazione?.tecnica?.editor_pantoni_consentiti]
  )
  const pantoneReference =
    preferredColorVariant !== 'pantone'
      ? []
      : tecnicaPantoniConsentiti.length > 0
      ? tecnicaPantoniConsentiti
      : fullPantoneReference

  const forcedSingleColorHex = useMemo(() => {
    const allowedColors = Array.isArray(colorLimits?.allowedColors) ? colorLimits.allowedColors : []
    if (allowedColors.length !== 1) return ''

    const normalizedAllowedColor = normalizeHexColor(allowedColors[0]?.hex || '')
    if (!normalizedAllowedColor) return ''

    return preferredColorVariant === 'pantone'
      ? getPantoneHex(normalizedAllowedColor, pantoneReference)
      : normalizedAllowedColor
  }, [colorLimits?.allowedColors, pantoneReference, preferredColorVariant])

  const isGlobalPantoniColorMode = useMemo(() => {
    // Se la tecnica è in modalità colori fissi e definisce
    // una palette `editor_pantoni_consentiti`, il canvas lavora in "colore globale".
    // Significa che tutti gli oggetti devono condividere sempre lo stesso colore.
    return (
      !!personalizzazioneData?.configurazione?.tecnica?.editor_modalita_colori_fissi &&
      tecnicaPantoniConsentiti.length > 0
    )
  }, [
    personalizzazioneData?.configurazione?.tecnica?.editor_modalita_colori_fissi,
    tecnicaPantoniConsentiti,
  ])

  const effectiveGlobalColorHex = useMemo(() => {
    // Colore unico "effettivo" per le logiche globali:
    // - priorità al colore forzato da allowedColors (se unico)
    // - fallback al primo allowed color quando non ci sono ancora object controls
    // - fallback al primo colore disponibile sugli oggetti quando siamo in global mode
    if (forcedSingleColorHex) return forcedSingleColorHex
    if (!isGlobalPantoniColorMode) return ''

    const normalizeByVariant = (value?: string) => {
      const normalized = normalizeHexColor(value || '')
      if (!normalized) return ''
      return preferredColorVariant === 'pantone'
        ? getPantoneHex(normalized, pantoneReference)
        : normalized
    }

    const normalizedAllowedColors = Array.isArray(colorLimits?.allowedColors)
      ? colorLimits.allowedColors.map((color) => normalizeByVariant(color?.hex)).filter(Boolean)
      : []
    const allowedColorsSet = new Set(normalizedAllowedColors)
    const fallbackAllowedColor = normalizedAllowedColors[0] || ''

    if (!objectControls?.length) {
      return fallbackAllowedColor
    }

    for (const control of objectControls || []) {
      if (control?.etype === 'text') {
        const textColor = normalizeByVariant(control?.colore?.value || control?.colore?.id || '')
        if (!textColor) continue
        if (allowedColorsSet.size > 0 && !allowedColorsSet.has(textColor)) continue
        return textColor
      }

      if (control?.etype === 'image') {
        const firstColor = control?.colori?.[0]
        const imageColor = normalizeByVariant(firstColor?.value || firstColor?.id || '')
        if (!imageColor) continue
        if (allowedColorsSet.size > 0 && !allowedColorsSet.has(imageColor)) continue
        return imageColor
      }
    }

    return fallbackAllowedColor
  }, [
    colorLimits?.allowedColors,
    forcedSingleColorHex,
    isGlobalPantoniColorMode,
    objectControls,
    pantoneReference,
    preferredColorVariant,
  ])

  const objectControlsIdsSignature = useMemo(
    () =>
      (objectControls || []).map((control) => `${control?.etype || ''}:${control?.id}`).join('|'),
    [objectControls]
  )
  const pantoneReferenceSignature = useMemo(
    () =>
      pantoneReference
        .map((pantone) => normalizeHexColor(pantone?.hex || ''))
        .filter(Boolean)
        .join('|'),
    [pantoneReference]
  )
  // Flag della tecnica: abilita solo la resa visuale traslucida, senza toccare il colore salvato.
  const isTranslucentFinishEnabled = useMemo(
    () => isTranslucentFinishEnabledForTecnica(personalizzazioneData?.configurazione?.tecnica),
    [personalizzazioneData?.configurazione?.tecnica?.editor_finitura_traslucida]
  )

  const selectedObject = useMemo(() => {
    return objectControls?.find((o) => o.isSelected)
  }, [objectControls])

  const isDirty = useMemo(() => {
    // fabric non salva nel json le proprietà undefined quindi le devo inserire per fare il confronto
    // escludo isSelected
    return !_.isEqual(
      objectControls.map((oc) => _.omit({ ...DEFAULT_OBJECT_CONTROL, ...oc }, ['isSelected'])),
      initialObjectControls.map((oc) =>
        _.omit({ ...DEFAULT_OBJECT_CONTROL, ...oc }, ['isSelected'])
      )
    )
  }, [initialObjectControls, objectControls])

  const resetDirty = useCallback((newObjectControls: any[]) => {
    // riallineo lo stato iniziale per il nuovo confronto, in questo modo se l'utente salva e poi fa un'altra modifica non viene confuso da un confronto con uno stato iniziale ormai obsoleto
    setInitialObjectControls(newObjectControls)
  }, [])

  // ============================================================================
  // Callback di utilità/core runtime
  // ============================================================================
  const applyTranslucentFinishToCanvasObjects = useCallback(() => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    applyTranslucentFinishToCanvasAndRender({
      canvas,
      enabled: isTranslucentFinishEnabled,
      specialObjectIds: SPECIAL_CANVAS_OBJECT_IDS,
    })
  }, [isTranslucentFinishEnabled])

  // ============================================================================
  // Effetti di sincronizzazione ref -> stato corrente
  // ============================================================================
  useEffect(() => {
    previewRef.current = preview
  }, [preview])

  useEffect(() => {
    preferredColorVariantRef.current = preferredColorVariant
  }, [preferredColorVariant])

  useEffect(() => {
    pantoneReferenceRef.current = pantoneReference
  }, [pantoneReference])

  useEffect(() => {
    areaDimensionsRef.current = {
      larghezzaAreaStampa,
      altezzaAreaStampa,
    }
  }, [larghezzaAreaStampa, altezzaAreaStampa])

  // ============================================================================
  // Callback viewport/sfondo/area stampa
  // ============================================================================
  // Il viewport reale coincide con lo spazio disponibile in stageMain.
  // Qui adattiamo il canvas Fabric a quel viewport e calcoliamo lo zoom minimo che
  // mantiene lo sfondo interamente visibile. Il refit viene riusato sia al mount sia
  // quando il layout cambia (es. preview mode che allarga il canvas senza un resize finestra).
  // Il centro iniziale del viewport usa l'area di stampa solo sull'asse orizzontale,
  // mentre in verticale resta ancorato allo sfondo per conservare l'inquadratura originale.
  const fitViewportToBackground = useCallback(() => {
    const canvas = fabricCanvasRef.current
    const container = canvasContainerRef.current
    const background = backgroundImageRef.current
    const areaStampa = areaStampaRef.current

    if (!canvas || !container || !background) return

    const viewportWidth = Math.round(container.clientWidth)
    const viewportHeight = Math.round(container.clientHeight)

    if (!viewportWidth || !viewportHeight) return

    // Evitiamo setDimensions inutili: Fabric fa lavoro extra e può introdurre flicker
    // se ridiamo sempre width/height anche quando il container non è cambiato.
    const viewportChanged =
      appliedViewportSizeRef.current.width !== viewportWidth ||
      appliedViewportSizeRef.current.height !== viewportHeight

    if (viewportChanged) {
      canvas.setDimensions({
        width: viewportWidth,
        height: viewportHeight,
      })
      appliedViewportSizeRef.current = {
        width: viewportWidth,
        height: viewportHeight,
      }
    }

    const backgroundWidth = background.getScaledWidth()
    const backgroundHeight = background.getScaledHeight()
    if (!backgroundWidth || !backgroundHeight) return

    // Lo zoom minimo è il "fit to screen" dello sfondo completo nel viewport disponibile.
    const zoom = Math.min(viewportWidth / backgroundWidth, viewportHeight / backgroundHeight)
    minZoomRef.current = Math.max(0.01, zoom)

    let focusCenterX = (background.left || 0) + backgroundWidth / 2
    const focusCenterY = (background.top || 0) + backgroundHeight / 2

    if (areaStampa) {
      areaStampa.setCoords()
      const areaBounds = areaStampa.getBoundingRect()
      focusCenterX = areaBounds.left + areaBounds.width / 2
    }

    // Ricostruiamo una viewport transform assoluta invece di un pan relativo:
    // in questo modo il risultato è deterministico anche dopo cambi di layout.
    const offsetX = viewportWidth / 2 - focusCenterX * minZoomRef.current
    const offsetY = viewportHeight / 2 - focusCenterY * minZoomRef.current

    canvas.setViewportTransform([minZoomRef.current, 0, 0, minZoomRef.current, offsetX, offsetY])
    canvas.calcOffset()
    canvas.requestRenderAll()
  }, [])

  const loadAreaStampaOnCanvas = async (
    canvas: fabric.Canvas,
    areaStampa: any,
    nextColoreId: number | string | null
  ) => {
    const areaStampaImageUrl = getAreaStampaImageUrl(areaStampa?.immagine_originale)
    if (!areaStampaImageUrl) return false

    canvas.backgroundImage = undefined

    const img = await fabric.FabricImage.fromURL(areaStampaImageUrl, undefined, {
      crossOrigin: 'anonymous',
    })

    img.set({
      customId: CANVAS_BACKGROUND_ID,
      left: CANVAS_CENTER_OFFSET,
      top: CANVAS_CENTER_OFFSET,
      selectable: false,
      evented: false,
      hoverCursor: 'default',
    })
    img.scaleToWidth(PRINT_AREA_SIZE)
    backgroundImageRef.current = img

    canvas
      .getObjects()
      .filter((obj: any) => SPECIAL_CANVAS_OBJECT_IDS.includes(obj.customId))
      .forEach((obj) => canvas.remove(obj))

    areaStampaRef.current = null
    canvas.add(img)
    canvas.sendObjectToBack(img)

    if (areaStampa?.info) {
      const info =
        typeof areaStampa.info === 'string' ? JSON.parse(areaStampa.info) : areaStampa.info
      const restoredObjects = await fabric.util.enlivenObjects([info])

      if (restoredObjects.length > 0) {
        const obj = restoredObjects[0] as fabric.Object
        obj.set({
          left: (obj.left || 0) + CANVAS_CENTER_OFFSET,
          top: (obj.top || 0) + CANVAS_CENTER_OFFSET,
          customId: 'area-stampa',
          selectable: false,
          evented: false,
          fill: 'transparent',
          strokeWidth: 8,
          strokeUniform: true,
          paintFirst: 'stroke',
          excludeFromImage: true,
        })
        areaStampaRef.current = obj
        canvas.add(areaStampaRef.current)
        addQuote()
      }
    }

    loadedAreaColorIdRef.current = nextColoreId
    return true
  }

  // ============================================================================
  // Effetti lifecycle canvas/layout
  // ============================================================================
  useEffect(() => {
    const init = async () => {
      // Initialize canvas only if the ref is available
      if (canvasElRef.current && personalizzazioneData) {
        // Create the Fabric canvas instance bound to the ref
        const canvas = new fabric.Canvas(canvasElRef.current, {
          height: 1,
          width: 1,
          // backgroundColor: 'lightgrey', // Optional background color
          controlsAboveOverlay: true,
          uniformScaling: true, // forziamo il resize uniforme
          uniScaleKey: null, // disabilitiamo il toggle da tastiera per evitare scaling non proporzionale
        })

        // Store the instance in the ref
        fabricCanvasRef.current = canvas

        const areaStampa =
          personalizzazioneData.configurazione.aree_stampa?.find(
            (a) => a.colore?.id === colore?.id
          ) || personalizzazioneData.configurazione.aree_stampa?.[0]

        // Attach Fabric event listeners
        const attachEventListeners = () => {
          canvas.on('object:moving', handleObjectMoving)
          canvas.on('object:scaling', handleObjectScaling)
          canvas.on('object:rotating', handleObjectRotating)
          canvas.on('object:added', handleObjectAdded)
          canvas.on('object:removed', handleObjectRemoved)
          canvas.on('object:modified', handleObjectModified)
          canvas.on('selection:created', handleSelectionChange)
          canvas.on('selection:updated', handleSelectionChange)
          canvas.on('selection:cleared', handleSelectionChange)
          canvas.on('mouse:wheel', handleWheel)
          canvas.on('mouse:down', handleMouseDown)
          canvas.on('mouse:move', handleMouseMove)
          canvas.on('mouse:up', handleMouseUp)
        }

        if (personalizzazioneData?.editor_state?.canvas_json) {
          attachEventListeners()

          const rawState = personalizzazioneData.editor_state.canvas_json
          const parsedState = typeof rawState === 'string' ? JSON.parse(rawState) : rawState
          await loadCanvas(parsedState)
          const hasLoadedAreaStampa = await loadAreaStampaOnCanvas(
            canvas,
            areaStampa,
            colore?.id ?? null
          )
          if (hasLoadedAreaStampa) {
            toggleCanvasOverlayVisibility(!previewRef.current)
            toggleCanvasObjectInteractivity(!(previewRef.current || measureModeRef.current))
            canvas.renderAll()
          }
          fitViewportToBackground()

          setInitialObjectControls(
            parsedState.objects
              .filter((obj: any) => !!obj.editorObjectControl)
              .map((obj: any) => obj.editorObjectControl || [])
          )
        } else {
          const hasLoadedAreaStampa = await loadAreaStampaOnCanvas(
            canvas,
            areaStampa,
            colore?.id ?? null
          )
          if (hasLoadedAreaStampa) {
            toggleCanvasOverlayVisibility(!previewRef.current)
            toggleCanvasObjectInteractivity(!(previewRef.current || measureModeRef.current))
            canvas.renderAll()
          }
          fitViewportToBackground()
          attachEventListeners()
        }

        // Ensure canvas renders the objects
        canvas.renderAll()
      }
    }
    init()
    // Cleanup function when the component unmounts
    return () => {
      if (fabricCanvasRef.current) {
        // Dispose the canvas instance
        fabricCanvasRef.current.off() // Rimuove tutti i listener in un colpo solo
        fabricCanvasRef.current.dispose()
        fabricCanvasRef.current = null // Clear the ref
        setObjectControls([]) // Clear any related state
        loghiRefs.current = [] // Clear logo references
        setPreviewImage(null) // Clear preview image
        setPreviewByColorId({})
        backgroundImageRef.current = null
        appliedViewportSizeRef.current = { width: 0, height: 0 }
        measurementAnchorRef.current = null
        measurementObjectRef.current = null
      }
    }
  }, [fitViewportToBackground, personalizzazioneData?.id])

  useEffect(() => {
    const handleResize = () => {
      fitViewportToBackground()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [fitViewportToBackground])

  useEffect(() => {
    const container = canvasContainerRef.current

    if (!container || typeof ResizeObserver === 'undefined') return

    // Il resize della finestra non copre i casi in cui cambia solo il layout interno
    // dell'editor (sidebar nascoste, preview mode, colonne che si aprono/chiudono).
    // Osserviamo quindi direttamente il container reale del canvas.
    const observer = new ResizeObserver(() => {
      fitViewportToBackground()
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [fitViewportToBackground])

  useEffect(() => {
    if (!isVisible) return

    // Il toggle di visibilità o preview può cambiare lo spazio disponibile nel layout.
    // Rimandiamo il refit al frame successivo per leggere le nuove misure del container
    // dopo il reflow del DOM.
    const frameId = window.requestAnimationFrame(() => {
      fitViewportToBackground()
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [fitViewportToBackground, isVisible, preview])

  useEffect(() => {
    toggleCanvasOverlayVisibility(!preview)
    toggleCanvasObjectInteractivity(!(preview || measureMode))
  }, [measureMode, preview])

  useEffect(() => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    if (!measureMode) {
      clearMeasurement()
    }

    canvas.defaultCursor = measureMode ? 'crosshair' : 'default'
    canvas.hoverCursor = measureMode ? 'crosshair' : 'move'
    canvas.requestRenderAll()
  }, [clearMeasurement, measureMode])

  const addQuote = useCallback(() => {
    const areaStampa = areaStampaRef.current
    const canvas = fabricCanvasRef.current
    if (!areaStampa || !canvas) return
    addPrintAreaQuote(canvas, areaStampa, larghezzaAreaStampa, altezzaAreaStampa)
  }, [larghezzaAreaStampa, altezzaAreaStampa])

  const getExportBounds = useCallback(
    () => getExportBoundsFromBackground(backgroundImageRef.current),
    []
  )

  const getCanvasBounds = useCallback(
    () => getCanvasBoundsFromBackground(backgroundImageRef.current),
    []
  )

  const getBackgroundOrigin = useCallback(
    () => getBackgroundOriginFromImage(backgroundImageRef.current),
    []
  )

  const getPrintAreaBoundsRelativeToBackground = useCallback(
    () =>
      getPrintAreaBoundsRelativeToBackgroundFromObjects(
        areaStampaRef.current,
        backgroundImageRef.current
      ),
    []
  )

  // ============================================================================
  // Callback interazione utente su canvas (pan/zoom/containment)
  // ============================================================================
  // <-- Zoom and Pan Handlers -->
  const handleWheel = useCallback(
    (opt) => {
      const canvas = fabricCanvasRef.current
      if (!canvas) return

      const delta = opt.e.deltaY
      const current = canvas.getZoom()
      const next = current * Math.pow(0.999, delta)
      const minZoom = minZoomRef.current || MIN_ZOOM

      const newZoom = next > MAX_ZOOM ? MAX_ZOOM : next < minZoom ? minZoom : next

      if (newZoom === current) {
        opt.e.preventDefault()
        opt.e.stopPropagation()
        return
      }

      const center = new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2)
      canvas.zoomToPoint(center, newZoom)

      if (newZoom <= minZoom) {
        fitViewportToBackground()
      } else {
        clampPan(canvas, getCanvasBounds())
      }

      opt.e.preventDefault()
      opt.e.stopPropagation()
      canvas.requestRenderAll()
    },
    [fitViewportToBackground, getCanvasBounds]
  )

  const startPan = (canvas, event) => {
    canvas.isPanning = true
    canvas.selection = false
    canvas.skipTargetFind = true
    canvas.discardActiveObject()
    canvas.lastX = event.clientX
    canvas.lastY = event.clientY
    canvas.setCursor('grabbing')
  }

  const handleMouseDown = (options) => {
    const canvas = fabricCanvasRef.current
    if (!canvas) {
      return
    }

    const isLeftClick = options?.e?.button === undefined || options?.e?.button === 0
    const clickedOnObject = !!options?.target

    if (!isLeftClick) {
      return
    }

    if (measureModeRef.current) {
      const pointer = canvas.getPointer(options.e)
      if (!pointer) return

      if (measurementAnchorRef.current) {
        renderMeasurement(measurementAnchorRef.current, pointer)
        measurementAnchorRef.current = null
      } else {
        clearMeasurement()
        measurementAnchorRef.current = pointer
        renderMeasurement(pointer, pointer)
      }

      options.e.preventDefault()
      options.e.stopPropagation()
      return
    }

    if (options?.e?.altKey || !clickedOnObject) {
      startPan(canvas, options.e)
      return
    }
  }

  const handleMouseMove = useCallback(
    (options) => {
      const canvas = fabricCanvasRef.current
      if (!canvas) {
        return
      }

      if (canvas.isPanning) {
        const e = options?.e
        if (!e || canvas.lastX == null || canvas.lastY == null) return

        const dx = e.clientX - canvas.lastX
        const dy = e.clientY - canvas.lastY

        if (dx || dy) {
          const zoom = canvas.getZoom()
          const speedFactor = 1 + Math.max(0, zoom - 1) * 3
          canvas.relativePan(new fabric.Point(dx * speedFactor, dy * speedFactor))
          clampPan(canvas, getCanvasBounds())
          canvas.requestRenderAll()
        }

        canvas.lastX = e.clientX
        canvas.lastY = e.clientY
        return
      }

      if (measureModeRef.current && measurementAnchorRef.current) {
        const pointer = canvas.getPointer(options.e)
        if (!pointer) return
        renderMeasurement(measurementAnchorRef.current, pointer)
      }
    },
    [clearMeasurement, getCanvasBounds, renderMeasurement]
  )

  const handleMouseUp = (options) => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    activeRotationGestureRef.current = null
    activeRotationSnapRef.current = null

    if (canvas.isPanning) {
      canvas.isPanning = false
      canvas.selection = !(previewRef.current || measureModeRef.current)
      canvas.skipTargetFind = previewRef.current || measureModeRef.current
      canvas.setCursor(measureModeRef.current ? 'crosshair' : 'default')
      return
    }
  }
  // <-- Fine Zoom and Pan Handlers -->

  const preserveBoundaries = (options) => {
    const obj = options.target
    const area = areaStampaRef.current
    if (!obj || !area) return

    // Recuperiamo l'azione. Se non specificata, usiamo 'typing'
    const action = options.transform?.action || options.action || 'typing'

    // Il testo aggiorna le sue metriche interne solo dopo un refresh esplicito;
    // senza questo passaggio i vincoli userebbero bounds già superati.
    if (TEXT_TYPES.includes(obj.type)) {
      refreshTextObjectDimensions(obj, action)
    }
    obj.setCoords()

    // --- CASO A: TYPING -> provo prima piccoli spostamenti, poi riduco il fontSize ---
    if (action === 'typing' && TEXT_TYPES.includes(obj.type)) {
      if (!isContained(obj, area)) {
        clampTextFontSizeToContainedArea(obj, area)
      }
    }

    // --- CASO B: clamp traslazionale SEMPRE nello spazio locale dell'area ---
    // Questo uniforma il comportamento per move/scale/rotate/typing quando
    // basta un semplice riassestamento di posizione per rientrare nei limiti.
    const { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area)
    // I confronti avvengono nello spazio dell'area di stampa, non nel canvas:
    // in questo modo i limiti seguono la rotazione dell'area e dell'oggetto.
    const { deltaX, deltaY } = getContainmentDeltaInAreaSpace(objectBounds, areaBounds)
    translateObjectInAreaSpace(obj, area, deltaX, deltaY)

    if (isContained(obj, area)) {
      // Dopo un clamp riuscito, questo diventa il nuovo stato valido.
      preTransformStateRef.current = getTransformState(obj)
      return
    }

    if (!preTransformStateRef.current) return

    // --- CASO C: fallback quando il solo riassestamento non basta ---
    if (isScalingAction(action)) {
      // Per lo scaling non torniamo direttamente indietro: clampiamo alla
      // massima trasformazione ancora valida così l'input "troppo grande"
      // viene tradotto nel massimo valore effettivamente applicabile.
      const attemptedState = getTransformState(obj)
      preTransformStateRef.current = clampScalingToContainedArea(
        obj,
        area,
        preTransformStateRef.current,
        attemptedState
      )
      return
    }

    if (isRotatingAction(action)) {
      // se con la semplice traslazione non rientra, proviamo a scalare l'oggetto
      // in modo da farlo rientrare nell'area di stampa, mantenendo la rotazione desiderata.
      const attemptedState = getTransformState(obj)
      preTransformStateRef.current = clampRotatingToContainedArea(
        obj,
        area,
        preTransformStateRef.current,
        attemptedState
      )
      return
    }

    // Per rotating (e altri casi non-scaling) manteniamo il revert all'ultimo
    // stato valido se non esiste una soluzione di sola traslazione.
    applyTransformState(obj, preTransformStateRef.current)
  }

  const toggleCanvasOverlayVisibility = (visible: boolean) => {
    if (!fabricCanvasRef.current) return

    const canvas = fabricCanvasRef.current
    canvas.getObjects().forEach((obj: any) => {
      if (PREVIEW_HIDDEN_OBJECT_IDS.includes(obj.customId)) {
        obj.set('visible', visible)
      }
    })

    canvas.requestRenderAll()
  }

  const toggleCanvasObjectInteractivity = (enabled: boolean) => {
    if (!fabricCanvasRef.current) return

    const canvas = fabricCanvasRef.current

    canvas.selection = enabled
    canvas.skipTargetFind = !enabled

    if (!enabled) {
      canvas.discardActiveObject()
    }

    canvas.getObjects().forEach((obj: any) => {
      if (!obj?.customId || SPECIAL_CANVAS_OBJECT_IDS.includes(obj.customId)) {
        return
      }

      obj.set({
        selectable: enabled,
        evented: enabled,
        hasControls: enabled,
        lockMovementX: !enabled,
        lockMovementY: !enabled,
        lockRotation: !enabled,
        lockScalingX: !enabled,
        lockScalingY: !enabled,
        hoverCursor: enabled ? 'move' : 'default',
      })

      if (enabled) {
        applyCornerOnlyControls(obj)
      }
    })

    canvas.requestRenderAll()
  }

  const exportCanvasDataUrl = useCallback(
    (includeBackground = true) =>
      exportCanvasDataUrlFromModule({
        canvas: fabricCanvasRef.current,
        background: backgroundImageRef.current,
        includeBackground,
        applyTranslucentFinish: applyTranslucentFinishToCanvasObjects,
        toggleCanvasOverlayVisibility,
        isPreviewVisible: previewRef.current,
      }),
    [applyTranslucentFinishToCanvasObjects]
  )

  const buildPreviewByColorId = useCallback(
    async (overlayDataUrl: string) => {
      try {
        const previewMap = await buildPreviewByColorIdFromModule({
          overlayDataUrl,
          forcedSingleColorHex,
          articoli,
          personalizzazioneData,
        })
        setPreviewByColorId(previewMap)
      } catch (error) {
        setPreviewByColorId({})
      }
    },
    [articoli, forcedSingleColorHex, personalizzazioneData]
  )

  // Aggiorna la preview "overlay only" usata in editor per mostrare gli oggetti
  // applicati sopra le immagini prodotto.
  const exportOverlayOnly = useCallback(() => {
    const overlayDataUrl = exportCanvasDataUrl(false)
    if (overlayDataUrl) {
      setPreviewImage(overlayDataUrl)
      buildPreviewByColorId(overlayDataUrl)
    } else {
      setPreviewImage(null)
      setPreviewByColorId({})
    }
  }, [buildPreviewByColorId, exportCanvasDataUrl])

  const getSecondaryPreviewItems = useCallback(() => {
    return getSecondaryPreviewItemsFromModule({
      articoli,
      coloreId: colore?.id,
      personalizzazioneData,
    })
  }, [
    articoli,
    colore?.id,
    personalizzazioneData?.configurazione?.aree_stampa,
    personalizzazioneData?.configurazione?.tecnica?.id,
  ])

  const composeMultiColorPreview = useCallback(
    async (mainDataUrl: string, overlayDataUrl: string) => {
      try {
        return await composeMultiColorPreviewFromModule({
          mainDataUrl,
          overlayDataUrl,
          secondaryItems: getSecondaryPreviewItems(),
          forcedSingleColorHex,
        })
      } catch (error) {
        console.error('Err composing multicolor preview', error)
        return mainDataUrl
      }
    },
    [forcedSingleColorHex, getSecondaryPreviewItems]
  )

  const getPreviewImage = useCallback(async () => {
    return getPreviewImageFromModule({
      exportWithBackground: () => exportCanvasDataUrl(true),
      exportOverlayOnly: () => exportCanvasDataUrl(false),
      compose: composeMultiColorPreview,
    })
  }, [composeMultiColorPreview, exportCanvasDataUrl])

  const handleDownloadPreview = useCallback(
    async (filenamePrefix: string) => {
      const dataURL = await getPreviewImage()
      if (!dataURL) return
      downloadPreviewDataUrl(dataURL, filenamePrefix)
    },
    [getPreviewImage]
  )

  // ============================================================================
  // Effetti sincronizzazione variant colore e preview
  // ============================================================================
  const debouncedExportOverlayOnly = useDebounceCallback(exportOverlayOnly, 200)

  // Wrapper locale che riusa il flusso "ufficiale" di cambio colore:
  // aggiorna Fabric, `_hiddenColorsData` e object controls React con le stesse
  // regole usate dagli input sidebar, evitando logiche duplicate negli effect.
  const handleColorChange = useCallback(
    (
      id: string | number,
      originalColorHex: string,
      newColorHex: string,
      syncOriginalValue = false
    ) => {
      handleColorChangeFromModule({
        canvas: fabricCanvasRef.current,
        id,
        originalColorHex,
        newColorHex,
        objectControls,
        isGlobalPantoniColorMode,
        specialCanvasObjectIds: SPECIAL_CANVAS_OBJECT_IDS,
        pantoneReference,
        syncOriginalValue,
        setObjectControls,
        onExportOverlayOnly: debouncedExportOverlayOnly,
      })
    },
    [debouncedExportOverlayOnly, isGlobalPantoniColorMode, objectControls, pantoneReference]
  )

  useEffect(() => {
    // Questo effect riallinea il "variant" colore richiesto dalla tecnica
    // corrente (`pantone` o `cmyk`) sia nei control React sia nel canvas Fabric.
    // Non sceglie un nuovo Pantone "più vicino": si limita a convertire i colori
    // esistenti nel formato atteso. Il remap verso la palette Pantone attiva
    // viene gestito dall'effect successivo.
    const canvas = fabricCanvasRef.current
    let hasChanges = false

    setObjectControls((prevControls) => {
      // Se tutti i control sono già nel variant corretto, oppure il canvas non
      // è ancora disponibile, non facciamo nulla per evitare render inutili.
      if (
        !prevControls.some((control) =>
          hasObjectControlVariantMismatch(control, preferredColorVariant)
        ) ||
        !canvas
      ) {
        return prevControls
      }

      const nextControls = prevControls.map((control) => {
        if (!hasObjectControlVariantMismatch(control, preferredColorVariant)) return control

        // Normalizziamo il control usando la palette Pantone corrente quando
        // serve, così sidebar e canvas ragionano sullo stesso set di colori.
        const nextControl = normalizeObjectControlColorVariant(
          control,
          preferredColorVariant,
          pantoneReference
        )
        const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === control.id)

        if (fabricObject) {
          // Testi e vettoriali seguono due strade diverse:
          // - testo: basta aggiornare il fill
          // - immagine/vettoriale: applichiamo una mappa old -> new su tutti i tracciati
          if (nextControl.etype === 'text' && control.colore?.value !== nextControl.colore?.value) {
            fabricObject.set('fill', nextControl.colore?.value)
          }

          if (nextControl.etype === 'image') {
            const colorMappings = buildColorMappings(control.colori, nextControl.colori)
            applyColorMappingsToFabricObject(fabricObject, colorMappings)
          }

          ;(fabricObject as any).editorObjectControl = nextControl
        }

        hasChanges = true
        return nextControl
      })

      return hasChanges ? nextControls : prevControls
    })

    if (hasChanges) {
      // Render e preview vengono aggiornati una sola volta a fine batch.
      canvas.requestRenderAll()
      debouncedExportOverlayOnly()
    }
  }, [debouncedExportOverlayOnly, pantoneReferenceSignature, preferredColorVariant])

  useEffect(() => {
    const canvas = fabricCanvasRef.current
    // Rileva il passaggio "tecnica con editor_forza_colore -> tecnica Pantone":
    // in quel caso il canvas visibile è ancora monocromatico, ma i nuovi match
    // devono partire dai colori originali dei singoli slot.
    const releasedForcedSingleColor =
      !!previousForcedSingleColorHexRef.current && !forcedSingleColorHex
    // Variante speciale del caso sopra: se la nuova tecnica è anche "globale",
    // non dobbiamo ripristinare i singoli colori finali, ma convergere subito
    // tutti gli oggetti verso un unico pantone target.
    const releasedForcedSingleColorTargetHex =
      releasedForcedSingleColor && isGlobalPantoniColorMode
        ? getPantoneHex(
            previousForcedSingleColorHexRef.current || effectiveGlobalColorHex,
            pantoneReference
          )
        : ''
    let hasRestoredOriginalColors = false

    if (!canvas || preferredColorVariant !== 'pantone' || pantoneReference.length === 0) {
      return
    }

    const restoreOriginalColorsIfNeeded = () => {
      // Ripristiniamo i colori base una sola volta per ciclo effetto. Serve a
      // ricostruire il materiale di partenza corretto prima di applicare il
      // nuovo nearest-pantone dentro la palette attiva.
      if (!releasedForcedSingleColor || hasRestoredOriginalColors) return

      objectControls.forEach((control) => {
        if (control.etype === 'image' && control.id) {
          resetObjectToOriginalColor({
            canvas,
            id: control.id,
          })
        }
      })

      hasRestoredOriginalColors = true
    }

    // Se cambia la palette attiva, oppure si rimuove un vincolo di colore singolo
    // (`editor_forza_colore`) ma il variant resta `pantone`, riallineiamo i colori
    // già presenti nel canvas al pantone più vicino della palette attiva.
    // Questo copre sia il passaggio a una palette limitata sia il ritorno
    // alla palette completa caricata dall'API. Qui aggiorniamo anche i riferimenti
    // "originali" (`originalValue`, `originalFill`, `originalStroke`) perché il
    // reset del monocromatico deve ripristinare colori validi per la tecnica attuale.
    // Il riallineamento degli "originali" parte sempre dal loro valore precedente,
    // così due slot diversi possono convergere a Pantoni ammessi diversi anche se
    // il valore corrente visibile nel canvas è temporaneamente uguale.
    if (releasedForcedSingleColorTargetHex) {
      restoreOriginalColorsIfNeeded()
      const firstId = objectControls.find((control) => !!control.id)?.id
      if (firstId) {
        // In modalità globale basta una singola invocazione: `handleColorChange`
        // propaga il colore a tutti gli oggetti del canvas.
        handleColorChange(
          firstId,
          releasedForcedSingleColorTargetHex,
          releasedForcedSingleColorTargetHex,
          true
        )
      }
      return
    }

    objectControls.forEach((control) => {
      const sourceControl = releasedForcedSingleColor
        ? control.etype === 'text'
          ? {
              ...control,
              colore: control.colore
                ? {
                    ...control.colore,
                    value: normalizeHexColor(
                      control.colore.originalValue ||
                        control.colore.id ||
                        control.colore.value ||
                        ''
                    ),
                  }
                : control.colore,
            }
          : {
              ...control,
              colori: (control.colori || []).map((color) => ({
                ...color,
                value: normalizeHexColor(color.originalValue || color.id || color.value || ''),
              })),
              removedBackgroundColor: control.removedBackgroundColor
                ? {
                    ...control.removedBackgroundColor,
                    value: normalizeHexColor(
                      control.removedBackgroundColor.originalValue ||
                        control.removedBackgroundColor.id ||
                        control.removedBackgroundColor.value ||
                        ''
                    ),
                  }
                : control.removedBackgroundColor,
            }
        : control

      const nextControl = normalizeObjectControlColorVariant(
        sourceControl,
        preferredColorVariant,
        pantoneReference
      )

      if (!control.id) return

      if (control.etype === 'text') {
        const currentColor = normalizeHexColor(control.colore?.value || control.colore?.id || '')
        const sourceColor = normalizeHexColor(
          sourceControl.colore?.value || sourceControl.colore?.id || ''
        )
        const nextColor = normalizeHexColor(
          nextControl.colore?.value || nextControl.colore?.id || ''
        )
        const colorToReplace = releasedForcedSingleColor ? sourceColor : currentColor

        if (colorToReplace && nextColor && currentColor !== nextColor) {
          restoreOriginalColorsIfNeeded()
          handleColorChange(control.id, colorToReplace, nextColor, true)
        }
        return
      }

      ;(nextControl.colori || []).forEach((nextColorControl, index) => {
        const currentColor = normalizeHexColor(
          control.colori?.[index]?.value || control.colori?.[index]?.id || ''
        )
        const sourceColor = normalizeHexColor(
          sourceControl.colori?.[index]?.value || sourceControl.colori?.[index]?.id || ''
        )
        const nextColor = normalizeHexColor(nextColorControl?.value || nextColorControl?.id || '')
        const colorToReplace = releasedForcedSingleColor ? sourceColor : currentColor

        // Quando usciamo da `editor_forza_colore` il match va fatto sullo slot
        // originale, non sul colore corrente del canvas, altrimenti tutti gli
        // slot collasserebbero verso la stessa chiave di sostituzione.
        if (colorToReplace && nextColor && currentColor !== nextColor) {
          restoreOriginalColorsIfNeeded()
          handleColorChange(control.id, colorToReplace, nextColor, true)
        }
      })

      const currentRemovedBackgroundColor = normalizeHexColor(
        control.removedBackgroundColor?.value || control.removedBackgroundColor?.id || ''
      )
      const sourceRemovedBackgroundColor = normalizeHexColor(
        sourceControl.removedBackgroundColor?.value ||
          sourceControl.removedBackgroundColor?.id ||
          ''
      )
      const nextRemovedBackgroundColor = normalizeHexColor(
        nextControl.removedBackgroundColor?.value || nextControl.removedBackgroundColor?.id || ''
      )
      const removedBackgroundColorToReplace = releasedForcedSingleColor
        ? sourceRemovedBackgroundColor
        : currentRemovedBackgroundColor

      if (
        removedBackgroundColorToReplace &&
        nextRemovedBackgroundColor &&
        currentRemovedBackgroundColor !== nextRemovedBackgroundColor
      ) {
        restoreOriginalColorsIfNeeded()
        handleColorChange(
          control.id,
          removedBackgroundColorToReplace,
          nextRemovedBackgroundColor,
          true
        )
      }
    })
  }, [
    forcedSingleColorHex,
    pantoneReference.length,
    pantoneReferenceSignature,
    effectiveGlobalColorHex,
    isGlobalPantoniColorMode,
    preferredColorVariant,
  ])

  useEffect(() => {
    previousForcedSingleColorHexRef.current = forcedSingleColorHex
  }, [forcedSingleColorHex])

  // ============================================================================
  // Callback sync stato Fabric <-> sidebar
  // ============================================================================
  const syncFabricToState = useCallback(
    (fabricObject: fabric.Object) => {
      const areaStampa = areaStampaRef.current
      if (!fabricObject || !fabricObject.customId || !areaStampa) return

      setObjectControls((prevControls) =>
        prevControls.map((oc) => {
          if (oc.id !== fabricObject.customId) return oc

          const visualDimensions = getVisualObjectDimensionsInCm(
            fabricObject,
            areaStampa,
            larghezzaAreaStampa,
            altezzaAreaStampa
          )

          const nextControl = {
            ...oc,
            // Dimensioni visive convertite in CM, inclusa l'eventuale rotazione.
            width: visualDimensions.width,
            height: visualDimensions.height,
            leftCm: visualDimensions.left,
            topCm: visualDimensions.top,
            // Proprietà fisiche
            angle: Math.round(fabricObject.angle || 0),
            left: Math.round(fabricObject.left),
            top: Math.round(fabricObject.top),
            // Proprietà testo (se presenti)
            text: (fabricObject as any).text || oc.text,
            fontSize: getEffectiveTextFontSize(fabricObject),
            fontFamily: (fabricObject as any).fontFamily || oc.fontFamily,
            fontWeight: (fabricObject as any).fontWeight || oc.fontWeight,
            fontStyle: (fabricObject as any).fontStyle || oc.fontStyle,
            fill: fabricObject.fill,
            underline: (fabricObject as any).underline,
            isSelected: fabricCanvasRef.current?.getActiveObject() === fabricObject,
          }

          ;(fabricObject as any).editorObjectControl = nextControl

          return nextControl
        })
      )

      debouncedExportOverlayOnly()
    },
    [larghezzaAreaStampa, altezzaAreaStampa]
  )

  const keepObjectInsideCurrentPrintArea = useCallback((obj: fabric.Object) => {
    const area = areaStampaRef.current
    if (!obj || !area) return

    if (TEXT_TYPES.includes(obj.type)) {
      refreshTextObjectDimensions(obj)
    } else {
      obj.setCoords()
    }

    const applyContainmentTranslation = () => {
      const { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area)
      const { deltaX, deltaY } = getContainmentDeltaInAreaSpace(objectBounds, areaBounds)
      translateObjectInAreaSpace(obj, area, deltaX, deltaY)

      return { areaBounds, objectBounds }
    }

    let { areaBounds, objectBounds } = applyContainmentTranslation()

    // Se il nuovo ingombro area-stampa è più piccolo dell'oggetto corrente,
    // riduciamo proporzionalmente prima di riposizionarlo.
    if (shrinkObjectToFitAreaBounds(obj, areaBounds, objectBounds)) {
      ;({ areaBounds, objectBounds } = applyContainmentTranslation())
    }

    if (!isContained(obj, area) && TEXT_TYPES.includes(obj.type)) {
      clampTextFontSizeToContainedArea(obj, area)
      applyContainmentTranslation()
    }

    obj.setCoords()
  }, [])

  // ============================================================================
  // Effetto cambio colore area stampa corrente
  // ============================================================================
  useEffect(() => {
    const nextColoreId = colore?.id ?? null
    const prevColoreId = loadedAreaColorIdRef.current

    if (String(prevColoreId ?? '') === String(nextColoreId ?? '')) return

    const canvas = fabricCanvasRef.current
    if (!canvas) return

    const areaStampa =
      personalizzazioneData?.configurazione?.aree_stampa?.find(
        (a) => a.colore?.id === colore?.id
      ) || personalizzazioneData?.configurazione?.aree_stampa?.[0]

    let cancelled = false

    const updateAreaStampa = async () => {
      const hasLoadedAreaStampa = await loadAreaStampaOnCanvas(canvas, areaStampa, nextColoreId)
      if (cancelled || !hasLoadedAreaStampa) return

      const userObjects = canvas
        .getObjects()
        .filter((obj: any) => !SPECIAL_CANVAS_OBJECT_IDS.includes(obj?.customId))

      userObjects.forEach((obj) => {
        keepObjectInsideCurrentPrintArea(obj)
        syncFabricToState(obj)
      })

      toggleCanvasOverlayVisibility(!previewRef.current)
      toggleCanvasObjectInteractivity(!(previewRef.current || measureModeRef.current))
      canvas.requestRenderAll()
      exportOverlayOnly()
      fitViewportToBackground()
    }

    void updateAreaStampa()

    return () => {
      cancelled = true
    }
  }, [
    colore?.id,
    exportOverlayOnly,
    fitViewportToBackground,
    keepObjectInsideCurrentPrintArea,
    personalizzazioneData?.configurazione?.aree_stampa,
    syncFabricToState,
  ])

  // ============================================================================
  // Event handlers Fabric (move/scale/rotate/add/remove/select)
  // ============================================================================
  // --- Event Handler for Moving Objects ---
  const handleObjectMoving = (options) => {
    preserveBoundaries(options)
    syncFabricToState(options.target) // Sync in tempo reale
  }

  // --- Event Handler for Scaling Objects ---
  const handleObjectScaling = (options) => {
    preserveBoundaries(options)
    syncFabricToState(options.target) // Sync in tempo reale
  }

  // --- Event Handler for Rotating Objects ---
  const handleObjectRotating = (options) => {
    const canvas = fabricCanvasRef.current
    const target = options?.target
    const event = options?.e
    const shouldSnapRotation = !!options?.e?.shiftKey
    const pointer = canvas && event ? canvas.getPointer(event) : null

    if (target && pointer) {
      const currentGestureState =
        activeRotationGestureRef.current?.target === target
          ? activeRotationGestureRef.current
          : null

      if (!currentGestureState) {
        // Primo frame della gesture: fissiamo pivot e offset una sola volta.
        const pivotCenter = target.getCenterPoint()
        const pointerAngle = getAngleFromCenterToPointer(pivotCenter, pointer)
        const currentAngle = normalizeAngle(target.angle || 0)
        const angleOffset = pointerAngle === null ? 0 : normalizeAngle(currentAngle - pointerAngle)

        activeRotationGestureRef.current = {
          target,
          pivotCenter,
          angleOffset,
        }
      }

      const nextGestureState =
        activeRotationGestureRef.current?.target === target
          ? activeRotationGestureRef.current
          : null
      if (nextGestureState) {
        // Frame successivi: l'angolo dipende dal puntatore rispetto al pivot iniziale,
        // non rispetto al nuovo centro eventualmente traslato dal containment.
        const pointerAngle = getAngleFromCenterToPointer(nextGestureState.pivotCenter, pointer)

        if (pointerAngle !== null) {
          const nextAngle = normalizeAngle(pointerAngle + nextGestureState.angleOffset)
          setObjectAngleAroundCenter(target, nextAngle)
        }
      }
    } else {
      activeRotationGestureRef.current = null
    }

    // Shift mantiene lo snap rigido a step fissi; senza shift la rotazione resta libera
    // ma si "aggancia" ai cardinali quando entra nella finestra magnetica.
    if (target && shouldSnapRotation) {
      activeRotationSnapRef.current = null
      setObjectAngleAroundCenter(target, getSnappedAngle(target.angle || 0, ROTATION_SNAP_STEP))
    } else if (target) {
      const currentSnapState =
        activeRotationSnapRef.current?.target === target ? activeRotationSnapRef.current : null
      const magneticSnapAngle = getMagneticSnapAngle(target.angle || 0, currentSnapState?.angle)

      if (magneticSnapAngle !== null) {
        activeRotationSnapRef.current = {
          target,
          angle: magneticSnapAngle,
        }
        setObjectAngleAroundCenter(target, magneticSnapAngle)
      } else {
        activeRotationSnapRef.current = null
      }
    }

    preserveBoundaries(options)
    syncFabricToState(target) // Sync in tempo reale
  }

  // --- Event Handler for Adding Objects ---
  const handleObjectAdded = (e) => {
    const canvas = fabricCanvasRef.current
    const areaStampa = areaStampaRef.current
    const addedObject = e.target
    const currentPreferredColorVariant = preferredColorVariantRef.current
    const {
      larghezzaAreaStampa: currentLarghezzaAreaStampa,
      altezzaAreaStampa: currentAltezzaAreaStampa,
    } = areaDimensionsRef.current
    if (!addedObject) return

    if (SPECIAL_CANVAS_OBJECT_IDS.includes((addedObject as any)?.customId)) return

    const detectedColors = TEXT_TYPES.includes(addedObject.type)
      ? []
      : extractObjectColors(
          addedObject,
          getObjectEditorMeta(addedObject),
          currentPreferredColorVariant,
          pantoneReferenceRef.current
        )

    // Ensure the object has a custom ID // TODO: non dovrebbe mai servire
    if (!addedObject.customId) {
      addedObject.customId = generateUniqueId()
    }

    // Uniformiamo il contenimento: qualsiasi oggetto che entra nel canvas
    // (inclusi restore da copy/paste/sync) viene prima riportato nella print area.
    keepObjectInsideCurrentPrintArea(addedObject)

    findBackgroundObjectFromModule(addedObject)

    // Avoid adding if already managed (e.g., during load from JSON) // Evita duplicati
    setObjectControls((prevControls) => {
      const isAlreadyTracked = prevControls.some((oc) => oc.id === addedObject.customId)
      if (isAlreadyTracked) return prevControls

      const savedControl = addedObject.editorObjectControl as EditorObjectType | undefined
      const controlMeta = savedControl?.editorMeta || {}
      const isText = TEXT_TYPES.includes(addedObject.type)
      const visualDimensions = getVisualObjectDimensionsInCm(
        addedObject,
        areaStampa,
        currentLarghezzaAreaStampa,
        currentAltezzaAreaStampa
      )

      // Creazione del set di controlli iniziale
      // Integriamo qui tutte le proprietà che vuoi vedere nel pannello laterale
      const newControl: EditorObjectType = normalizeObjectControlColorVariant(
        {
          ...(savedControl || {}),
          id: addedObject.customId,
          etype: isText ? 'text' : 'image',
          // name: `${addedObject.type} ${prevControls.length + 1}`,
          isSelected: canvas?.getActiveObject() === addedObject,

          // Dimensioni visive (Conversione PX -> CM), quindi coerenti con la rotazione.
          width: visualDimensions.width,
          height: visualDimensions.height,
          leftCm: visualDimensions.left,
          topCm: visualDimensions.top,

          // Posizione e Rotazione
          angle: Math.round(addedObject.angle || 0),
          left: Math.round(addedObject.left),
          top: Math.round(addedObject.top),

          // Stile e Colore
          fill: addedObject.fill,

          // Proprietà specifiche per il Testo (se l'oggetto è un testo)
          text: (addedObject as any).text || savedControl?.text || '',
          fontFamily: (addedObject as any).fontFamily || savedControl?.fontFamily,
          fontSize: (addedObject as any).fontSize || savedControl?.fontSize,
          fontWeight: (addedObject as any).fontWeight || savedControl?.fontWeight,
          fontStyle: (addedObject as any).fontStyle || savedControl?.fontStyle,
          underline: (addedObject as any).underline ?? savedControl?.underline,

          colori: (savedControl?.colori?.length ? savedControl.colori : detectedColors) || [],
          colore: savedControl?.colore || controlMeta?.colore,
          image: savedControl?.image || controlMeta?.image,
          filename: savedControl?.filename || controlMeta?.filename,
          isVector: savedControl?.isVector ?? controlMeta?.is_vector,
          hasBackground: savedControl?.hasBackground ?? addedObject?.hasBackground,
          noBackground: savedControl?.noBackground ?? addedObject?.noBackground,
          monochrome: savedControl?.monochrome ?? addedObject?.monochrome,
          removedBackgroundColor: savedControl?.removedBackgroundColor,
          removedBackgroundIndex: savedControl?.removedBackgroundIndex,
          editorMeta: controlMeta,
        },
        currentPreferredColorVariant,
        pantoneReferenceRef.current
      )

      ;(addedObject as any).editorObjectControl = newControl

      return [...prevControls, newControl]
    })

    // Registrazione nei riferimenti per l'analisi del colore
    if (!loghiRefs.current.find((obj) => (obj as any).customId === addedObject.customId)) {
      loghiRefs.current.push(addedObject)
    }

    freezeOriginalColors(addedObject)
    applyCornerOnlyControls(addedObject)
    toggleCanvasObjectInteractivity(!(previewRef.current || measureModeRef.current))

    debouncedExportOverlayOnly()
  }

  // --- Event Handler for Modifying Objects ---
  const handleObjectModified = (options) => {
    // Fine gesture: resettiamo lo stato temporaneo del pivot.
    activeRotationGestureRef.current = null
    activeRotationSnapRef.current = null
    void syncHiddenColorsDataTransform(options.target)
    syncFabricToState(options.target) // Sync in tempo reale
  }

  // --- Event Handler for Removing Objects ---
  const handleObjectRemoved = (e) => {
    const removedObject = e.target

    // Evitiamo di processare l'area di stampa se venisse rimossa per errore
    if (!removedObject || !removedObject.customId) return
    if (SPECIAL_CANVAS_OBJECT_IDS.includes(removedObject.customId)) return

    // Rimuovi l'oggetto dallo stato dei controlli UI
    setObjectControls((prevControls) =>
      prevControls.filter((oc) => oc.id !== removedObject.customId)
    )

    // Rimuovi l'oggetto dai riferimenti dei loghi (fondamentale per la gestione colori)
    loghiRefs.current = loghiRefs.current.filter(
      (obj) => (obj as any).customId !== removedObject.customId
    )

    // Reset dello stato pre-trasformazione (opzionale, per pulizia)
    preTransformStateRef.current = null

    debouncedExportOverlayOnly()
  }

  // --- Event Handler for Selection Changes ---
  const handleSelectionChange = () => {
    const canvas = fabricCanvasRef.current
    const area = areaStampaRef.current
    const activeObjects = canvas.getActiveObjects() // Gets all selected objects
    const activeIds = activeObjects.map((obj: any) => obj.customId).filter((id) => id)

    if (activeObjects.length === 1) {
      // salvo lo stato valido
      storeCurrentTransformStateIfValid(activeObjects[0], area, preTransformStateRef)
    }
    if (activeObjects.length !== 1) {
      // Se cambia la selezione, la gesture attiva non è più valida.
      activeRotationGestureRef.current = null
    }

    setObjectControls((prevControls) =>
      prevControls.map((oc) => ({
        ...oc,
        isSelected: activeIds.includes(oc.id),
      }))
    )
  }

  // ============================================================================
  // Callback azioni oggetto (delete/align/property/action dispatcher)
  // ============================================================================
  const removeFabricObject = useCallback(
    (object: fabric.Object | null) =>
      removeFabricObjectFromModule({
        canvas: fabricCanvasRef.current,
        object,
        loghiRefs,
      }),
    []
  )

  // --- Event Handler for Deleting Objects ---
  const handleDeleteObject = useCallback((id) => {
    deleteFabricObjectById({
      canvas: fabricCanvasRef.current,
      id,
      loghiRefs,
    })
  }, [])

  const handleDeleteActiveObjects = useCallback(() => {
    return deleteActiveFabricObject({
      canvas: fabricCanvasRef.current,
      loghiRefs,
    })
  }, [])

  // --- Alignment Handler ---
  const handleAlignObject = useCallback(
    (id, alignment) => {
      alignFabricObject({
        canvas: fabricCanvasRef.current,
        areaStampa: areaStampaRef.current,
        id,
        alignment,
        onSyncFabricToState: syncFabricToState,
      })
    },
    [syncFabricToState]
  )

  const handlePropertyChange = useCallback(
    (id, property, value) => {
      applyObjectPropertyChange({
        canvas: fabricCanvasRef.current,
        areaStampa: areaStampaRef.current,
        id,
        property,
        value,
        larghezzaAreaStampa,
        altezzaAreaStampa,
        preTransformStateRef,
        onPreserveBoundaries: preserveBoundaries,
        onSyncFabricToState: syncFabricToState,
      })
    },
    [larghezzaAreaStampa, altezzaAreaStampa, syncFabricToState]
  )

  const debouncedPropertyChange = useDebounceCallback(handlePropertyChange, 150)

  const handleInputChange = useCallback(
    (id: string, field: string, value: any) => {
      // Aggiornamento immediato dello stato React (UI fluida)
      setObjectControls((prevControls) =>
        prevControls.map((oc) => (oc.id === id ? { ...oc, [field]: value } : oc))
      )

      // Chiamata alla logica Fabric per aggiornare il canvas
      // Passiamo il valore direttamente per evitare di aspettare il re-render di React
      debouncedPropertyChange(id, field, value) // AGGIORNAMENTO RITARDATO: Il canvas di Fabric si aggiorna dopo 150ms
    },
    [debouncedPropertyChange]
  )

  // l'unico metodo da chiamare da fuori (handleInputChange, handleAlignObject, handleDeleteObject sono solo interni)
  const handleObjectAction = useCallback(
    (id, action, payload) => {
      dispatchObjectAction({
        id,
        action,
        payload,
        onProperty: handleInputChange,
        onAlign: handleAlignObject,
        onDelete: handleDeleteObject,
      })
    },
    [handleAlignObject, handleDeleteObject, handleInputChange]
  )

  // ============================================================================
  // Callback caricamento oggetti (image/text/svg)
  // ============================================================================
  const loadSvgToCanvas = useCallback(
    (objects, options, id, meta = {}) => {
      loadSvgToCanvasFromModule({
        canvas: fabricCanvasRef.current,
        areaStampa: areaStampaRef.current,
        preview: previewRef.current,
        preferredColorVariant,
        effectiveGlobalColorHex,
        pantoneReference,
        objects,
        options,
        id,
        meta,
      })
    },
    [effectiveGlobalColorHex, pantoneReference, preferredColorVariant]
  )

  const loadSvgFromUrl = useCallback(
    (url: string, id: number, meta = {}) => {
      return loadSvgFromUrlFromModule({
        url,
        id,
        meta,
        onLoaded: loadSvgToCanvas,
      })
    },
    [loadSvgToCanvas]
  )

  const loadImage = useCallback(
    (image: EditorImageType) => {
      return loadImageObject({
        image,
        onLoadSvgFromUrl: loadSvgFromUrl,
      })
    },
    [loadSvgFromUrl]
  )

  const loadText = useCallback(
    (text: EditorTextType) => {
      loadTextObject({
        canvas: fabricCanvasRef.current,
        areaStampa: areaStampaRef.current,
        preview: previewRef.current,
        text,
        preferredColorVariant,
        effectiveGlobalColorHex,
        pantoneReference,
      })
    },
    [effectiveGlobalColorHex, pantoneReference, preferredColorVariant]
  )

  const freezeOriginalColors = freezeOriginalColorsFromModule

  const resetToOriginalColor = useCallback((id: string) => {
    resetObjectToOriginalColor({
      canvas: fabricCanvasRef.current,
      id,
    })
  }, [])

  // ============================================================================
  // Callback gestione colori
  // ============================================================================
  useEffect(() => {
    // Ad ogni cambio di colore principale e/o di tecnica,
    // se c'è un solo colore forzato, allinea tutti i controlli a quel colore (sia testo che immagini)

    // 1. Validazione iniziale e determinazione del colore target
    const targetColor = isGlobalPantoniColorMode ? effectiveGlobalColorHex : forcedSingleColorHex
    if (!targetColor || !objectControls?.length) return

    // 2. Helper per estrarre il colore attuale da un oggetto/slot
    const getCurrentColor = (obj: any) => normalizeHexColor(obj?.value || obj?.id || '')

    // --- MODALITÀ GLOBALE ---
    if (isGlobalPantoniColorMode) {
      const hasMismatchedColor = objectControls.some((oc) => {
        if (oc.etype === 'text') return getCurrentColor(oc.colore) !== targetColor
        return (oc.colori || []).some((c) => getCurrentColor(c) !== targetColor)
      })

      // Se troviamo colori disallineati, rilanciamo un singolo update globale che sincronizza tutto
      // (handleColorChange in modalità globale aggiorna tutti gli oggetti in automatico).
      if (hasMismatchedColor) {
        const firstId = objectControls.find((oc) => !!oc.id)?.id
        if (firstId) handleColorChange(firstId, targetColor, targetColor)
      }
      return
    }

    // --- MODALITÀ FORCED SINGLE COLOR ---
    objectControls.forEach((oc) => {
      if (!oc.id) return

      if (oc.etype === 'text') {
        const current = getCurrentColor(oc.colore)
        if (current && current !== targetColor) {
          handleColorChange(oc.id, current, targetColor)
        }
      } else if (oc.etype === 'image') {
        // Per le immagini, controlliamo ogni slot colore
        oc.colori?.forEach((c) => {
          const current = getCurrentColor(c)
          const original = normalizeHexColor(c.originalValue || c.id || c.value || '')

          if (current && current !== targetColor && original) {
            handleColorChange(oc.id, original, targetColor)
          }
        })
      }
    })
  }, [
    forcedSingleColorHex,
    effectiveGlobalColorHex,
    isGlobalPantoniColorMode,
    activeCanvasId,
    objectControlsIdsSignature,
  ])

  const handleMonochromaticChange = useCallback(
    (id: string, checked: boolean) => {
      handleMonochromaticChangeFromModule({
        canvas: fabricCanvasRef.current,
        id,
        checked,
        objectControls,
        pantoneReference,
        setObjectControls,
        onResetToOriginalColor: resetToOriginalColor,
        onExportOverlayOnly: debouncedExportOverlayOnly,
      })
    },
    [debouncedExportOverlayOnly, objectControls, pantoneReference, resetToOriginalColor]
  )

  const handleNoBackgroundChange = useCallback(
    (id: string, checked: boolean) => {
      handleNoBackgroundChangeFromModule({
        canvas: fabricCanvasRef.current,
        id,
        checked,
        objectControls,
        setObjectControls,
        onExportOverlayOnly: debouncedExportOverlayOnly,
      })
    },
    [objectControls]
  )

  const handleColorVisibilityChange = useCallback(
    async (id: string | number, colorId: string, visible: boolean) => {
      await handleColorVisibilityChangeFromModule({
        canvas: fabricCanvasRef.current,
        areaStampa: areaStampaRef.current,
        id,
        colorId,
        visible,
        objectControls,
        loghiRefs,
        setObjectControls,
        onSyncFabricToState: syncFabricToState,
        onExportOverlayOnly: debouncedExportOverlayOnly,
      })
    },
    [debouncedExportOverlayOnly, objectControls, syncFabricToState]
  )

  // ============================================================================
  // Callback serializzazione / copy-paste / sync
  // ============================================================================
  const serializeCanvasJson = useCallback(
    (excludeSpecialObjects = false) => {
      return serializeCanvasJsonFromModule({
        canvas: fabricCanvasRef.current,
        objectControls,
        getBackgroundOrigin,
        getPrintAreaBoundsRelativeToBackground,
        excludeSpecialObjects,
      })
    },
    [getBackgroundOrigin, getPrintAreaBoundsRelativeToBackground, objectControls]
  )

  const saveCanvasJson = useCallback(() => {
    // Export completo usato per il salvataggio a DB/API: manteniamo anche gli oggetti tecnici
    // come area-stampa/quota, così il payload resta compatibile con il comportamento originale.
    return serializeCanvasJson(false)
  }, [serializeCanvasJson])

  const saveTransferCanvasJson = useCallback(() => {
    // Export "trasferibile" per copy/paste e sync: escludiamo gli oggetti tecnici locali
    // del canvas, perché sul target devono restare quelli propri della sua posizione.
    return serializeCanvasJson(true)
  }, [serializeCanvasJson])

  const loadCanvas = async (canvasJson) => {
    await loadCanvasFromJsonFromModule({
      canvas: fabricCanvasRef.current,
      canvasJson,
      onAreaStampaRestored: (instance) => {
        areaStampaRef.current = instance
      },
    })
  }

  const replaceCanvasJson = useCallback(
    async (canvasJson) => {
      await replaceCanvasJsonFromModule({
        canvas: fabricCanvasRef.current,
        canvasJson,
        getPrintAreaBoundsRelativeToBackground,
        setObjectControls,
        loghiRefs,
        preTransformStateRef,
        toggleCanvasOverlayVisibility,
        toggleCanvasObjectInteractivity,
        isPreviewVisible: previewRef.current,
        onExportOverlayOnly: exportOverlayOnly,
      })
    },
    [exportOverlayOnly, getPrintAreaBoundsRelativeToBackground]
  )

  // ============================================================================
  // Effetti finali: sync colore globale, registrazione azioni e bridge verso context
  // ============================================================================
  const editorActions = useMemo<CanvasActions>(
    () => ({
      handleObjectAction,
      loadText,
      loadImage,
      getPreviewImage,
      handleDownloadPreview,
      handleColorChange,
      handleMonochromaticChange,
      handleNoBackgroundChange,
      handleColorVisibilityChange,
      saveCanvasJson,
      saveTransferCanvasJson,
      replaceCanvasJson,
      resetDirty,
    }),
    [
      handleObjectAction,
      loadText,
      loadImage,
      getPreviewImage,
      handleDownloadPreview,
      handleColorChange,
      handleMonochromaticChange,
      handleNoBackgroundChange,
      handleColorVisibilityChange,
      saveCanvasJson,
      saveTransferCanvasJson,
      replaceCanvasJson,
      resetDirty,
    ]
  )

  useEffect(() => {
    const canvasId = personalizzazioneData?.id
    if (!canvasId) return

    registerActions(canvasId, editorActions)
    return () => unregisterActions(canvasId)
  }, [personalizzazioneData?.id, registerActions, unregisterActions, editorActions])

  useEffect(() => {
    const isCurrentCanvasActive =
      String(activeCanvasId ?? '') === String(personalizzazioneData?.id ?? '')

    if (!isCurrentCanvasActive) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!['Delete', 'Backspace'].includes(event.key)) return
      if (event.ctrlKey || event.metaKey || event.altKey) return

      const target = event.target
      if (target instanceof HTMLElement) {
        const tagName = target.tagName
        if (
          target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName) ||
          target.closest('[contenteditable="true"]')
        ) {
          return
        }
      }

      const hasDeletedObjects = handleDeleteActiveObjects()
      if (hasDeletedObjects) {
        event.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeCanvasId, handleDeleteActiveObjects, personalizzazioneData?.id])

  useEffect(() => {
    // Rende il finish resiliente a load/paste/sync e a modifiche degli oggetti lato sidebar.
    applyTranslucentFinishToCanvasObjects()
  }, [applyTranslucentFinishToCanvasObjects, objectControlsIdsSignature])

  useEffect(() => {
    if (personalizzazioneData?.id) {
      updateCanvasData(
        personalizzazioneData.id,
        objectControls,
        previewImage,
        isDirty,
        previewByColorId
      )
    }
  }, [objectControls, previewImage, isDirty, previewByColorId])

  // ============================================================================
  // API pubblica del hook
  // ============================================================================
  return {
    canvasContainerRef,
    canvasElRef,
    fabricCanvasRef,
    objectControls,
    loadText,
    loadImage,
    getPreviewImage,
    handleDownloadPreview,
    handleObjectAction,
    handleColorChange,
    handleColorVisibilityChange,
    selectedObject,
    isDirty,
    resetDirty,
  }
}
